import type { GridColDef } from '@mui/x-data-grid'
import { formatNumber } from '@/utils/money'
import { titleCaseName } from '@/utils/text'

export type SaleReferencePageKey =
  | 'CertificationUpdates'
  | 'Claims'
  | 'Consignee'

export type SaleReferenceValueKind = 'text' | 'number' | 'date' | 'datetime'

export interface SaleReferenceColumnConfig {
  field: string
  headerName: string
  kind?: SaleReferenceValueKind
  precision?: number
  width?: number
  minWidth?: number
  flex?: number
  align?: 'left' | 'center' | 'right'
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
  /** Wrap cell text (auto row height) thay vì ellipsis 1 dòng — bật cho cột chữ dài
      (tên KH/NCC, address, note, description...) kể cả khi user chưa resize cột. */
  wrap?: boolean
}

export interface SaleReferencePageConfig {
  pageKey: SaleReferencePageKey
  label: string
  sourceForm: string
  sourceQuery: string
  limit?: number
  columns: SaleReferenceColumnConfig[]
}

export const SALE_REFERENCE_PAGE_ORDER: SaleReferencePageKey[] = [
  'CertificationUpdates',
  'Claims',
  'Consignee',
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatAccessDate(value: unknown): string {
  if (value == null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = `${value.getDate()}`.padStart(2, '0')
    const month = ACCESS_MONTHS[value.getMonth()]
    const year = `${value.getFullYear()}`
    return `${day}-${month}-${year}`
  }
  const raw = String(value).trim()
  if (!raw) return ''
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  const day = `${parsed.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[parsed.getMonth()]
  const year = `${parsed.getFullYear()}`
  return `${day}-${month}-${year}`
}

export function formatAccessDateTime(value: unknown): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(String(value).trim())
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

export function formatSaleReferenceValue(
  value: unknown,
  kind: SaleReferenceValueKind = 'text',
  precision = 0,
): string {
  if (value == null || value === '') return ''
  if (kind === 'date') return formatAccessDate(value)
  if (kind === 'datetime') return formatAccessDateTime(value)
  if (kind === 'number') {
    if (typeof value === 'number') {
      return formatNumber(value, precision)
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? String(value) : formatNumber(parsed, precision)
  }
  return String(value)
}

export function buildSaleReferenceColumns(columns: SaleReferenceColumnConfig[]): GridColDef<Record<string, unknown>>[] {
  return columns.map((column) => {
    const align = column.align ?? (column.kind === 'number' ? 'right' : 'left')
    return {
      field: column.field,
      headerName: column.headerName,
      width: column.width,
      minWidth: column.minWidth,
      flex: column.flex,
      sortable: true,
      filterable: false,
      align,
      headerAlign: align,
      valueFormatter: (value: unknown) => {
        if (column.proper) return titleCaseName(value)
        return column.kind === 'number'
          ? formatSaleReferenceValue(value, 'number', column.precision ?? 0)
          : formatSaleReferenceValue(value, column.kind)
      },
    }
  })
}

export function filterSaleReferenceRows(
  rows: Record<string, unknown>[],
  search: string,
  columns: SaleReferenceColumnConfig[],
): Record<string, unknown>[] {
  const term = search.trim().toLowerCase()
  if (!term) return rows
  return rows.filter((row) =>
    columns.some((column) =>
      (column.proper
        ? titleCaseName(row[column.field])
        : formatSaleReferenceValue(row[column.field], column.kind, column.precision ?? 0)
      )
        .toLowerCase()
        .includes(term),
    ),
  )
}

export const SALE_REFERENCE_PAGE_CONFIGS: Record<SaleReferencePageKey, SaleReferencePageConfig> = {
  CertificationUpdates: {
    pageKey: 'CertificationUpdates',
    label: 'CertificationUpdates',
    sourceForm: 'frmContractSalesQryRefCertificationChange',
    sourceQuery: 'ContractSalesQryRefCertifitcationChange',
    limit: 1000000,
    columns: [
      { field: 'changed_date', headerName: 'Changed Date', kind: 'date', width: 140 },
      { field: 'premium_value', headerName: 'Premium Value', kind: 'number', precision: 4, width: 160 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Claims: {
    pageKey: 'Claims',
    label: 'Claims',
    sourceForm: 'frmContractSalesQryRefClaimCondition',
    sourceQuery: 'ContractSalesQryRefClaimCondition',
    limit: 1000000,
    columns: [
      { field: 'organization', headerName: 'Organization', minWidth: 200, flex: 1, wrap: true },
      { field: 'weighing_date', headerName: 'Weighing Date', kind: 'number', precision: 0, width: 140 },
      { field: 'weight_claim', headerName: 'Weight Claim', kind: 'number', precision: 0, width: 140 },
      { field: 'quality_claims', headerName: 'Quality Claims', kind: 'number', precision: 0, width: 150 },
      { field: 'others', headerName: 'Others', kind: 'number', precision: 0, width: 120 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Consignee: {
    pageKey: 'Consignee',
    label: 'Consignee',
    sourceForm: 'frmContractSalesQryRefConsignee',
    sourceQuery: 'ContractSalesQryRefConsignee',
    limit: 1000000,
    columns: [
      { field: 'consignee_code', headerName: 'Code', width: 140 },
      { field: 'consignee_name', headerName: 'Name', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
}

export function getSaleReferencePageConfig(pageKey: SaleReferencePageKey): SaleReferencePageConfig {
  return SALE_REFERENCE_PAGE_CONFIGS[pageKey]
}
